import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FloatButton, Input, Button, Spin } from 'antd';
import {
  UserOutlined,
  CloseOutlined,
  SendOutlined,
  DeleteOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { baseURL } from '../API/url';
import GinkgoHouseIcon from '../image/GinkgoHouse.jpg';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type ChatRole = 'user' | 'ai';

interface ChatTurn {
  role: ChatRole;
  content: string;
  timestamp?: string;
}

interface ChatbotWidgetProps {
  userEmail: string;
  userType?: string;
}

const defaultRecommendationsByUserType: Record<string, string[]> = {
  admin: [
    'How much redemptions?',
    'How much commission and trailer fees?',
  ],
  emd: [
    'How much redemptions?',
    'How much commission and trailer fees?',
  ],
  advisor: [
    'How much redemptions?',
    'How much commission and trailer fees?',
  ],
};

const getDefaultRecommendations = (userType?: string): string[] =>
  defaultRecommendationsByUserType[userType || 'admin'] || defaultRecommendationsByUserType.admin;

const normalizeReplyText = (raw: any): string => {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    const parts = raw
      .map((i: any) => (typeof i === 'string' ? i : i?.text || ''))
      .filter(Boolean);
    if (parts.length) return parts.join('\n');
  }
  if (raw && typeof raw === 'object' && typeof raw.text === 'string') return raw.text;
  try { return JSON.stringify(raw, null, 2); } catch { return String(raw || ''); }
};

const getDayKey = (timestamp?: string): string => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const formatChatDateLabel = (timestamp?: string): string => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((today.getTime() - messageDay.getTime()) / (1000 * 60 * 60 * 24));

  const dateLabel =
    dayDiff === 0
      ? 'Today'
      : dayDiff === 1
        ? 'Yesterday'
        : date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          });

  return dateLabel;
};

const formatChatTimeLabel = (timestamp?: string): string => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/* ------------------------------------------------------------------ */
/*  Inline styles – no dependency on Ant Design X or Card body flex   */
/* ------------------------------------------------------------------ */
const PANEL: React.CSSProperties = {
  position: 'fixed',
  right: 24,
  bottom: 90,
  width: 400,
  height: '70vh',
  minHeight: 520,
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  boxShadow: '0 6px 24px rgba(0,0,0,.15)',
  background: '#fff',
  overflow: 'hidden',
};

const HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 14px',
  background: '#1677ff',
  color: '#fff',
  fontSize: 15,
  fontWeight: 600,
  flexShrink: 0,
};

const MSG_LIST: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '8px 12px',
  minHeight: 0,          /* critical for flex overflow */
};

const BUBBLE_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: 10,
  gap: 8,
};

const AVATAR: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  flexShrink: 0,
};

const BUBBLE_TEXT: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 10,
  maxWidth: '78%',
  fontSize: 13,
  lineHeight: 1.45,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

const INPUT_ROW: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  padding: '8px 12px',
  borderTop: '1px solid #f0f0f0',
  flexShrink: 0,
};

/* ------------------------------------------------------------------ */

/**
 * Scrape the visible page data and produce a Markdown summary the AI model
 * can use as context.  Captures layout, active elements, statistics, tables,
 * buttons, radio selections, form values, and more.
 * Capped at ~4 000 chars to fit comfortably in the prompt.
 */
function scrapePageContext(userType?: string): string {
  const md: string[] = [];

  /* ── Page identity ──────────────────────────────────────────────── */
  md.push(`# ${document.title || 'Ginkgo'}`);
  md.push(`**Route:** ${window.location.pathname}`);
  if (userType) {
    md.push(`**User Type:** ${userType}`);
  }

  /* ── Page headings (h2–h4) ──────────────────────────────────────── */
  const headings: string[] = [];
  document.querySelectorAll('h2, h3, h4').forEach((h) => {
    const t = h.textContent?.trim();
    // skip chatbot's own heading
    if (t && !/chat assistant/i.test(t)) headings.push(t);
  });
  if (headings.length) {
    md.push('');
    md.push('## Page Sections');
    headings.forEach((h) => md.push(`- ${h}`));
  }

  /* ── Tabs & active tab ──────────────────────────────────────────── */
  const tabs: string[] = [];
  let activeTabLabel = '';
  document.querySelectorAll('.ant-tabs-tab').forEach((tab) => {
    const label = tab.querySelector('.ant-tabs-tab-btn')?.textContent?.trim() || '';
    if (!label) return;
    const isActive = tab.classList.contains('ant-tabs-tab-active');
    tabs.push(isActive ? `**${label}** (active)` : label);
    if (isActive) activeTabLabel = label;
  });
  if (tabs.length) {
    md.push('');
    md.push('## Tabs');
    md.push(tabs.join(' | '));
  }

  /* ── Statistics / KPI cards ─────────────────────────────────────── */
  const stats: string[] = [];
  document.querySelectorAll('.ant-statistic').forEach((el) => {
    const title = el.querySelector('.ant-statistic-title')?.textContent?.trim();
    const prefix = el.querySelector('.ant-statistic-content-prefix')?.textContent?.trim() || '';
    const value = el.querySelector('.ant-statistic-content-value')?.textContent?.trim();
    if (title && value) stats.push(`| ${title} | ${prefix}${value} |`);
  });
  // Also capture Title-level-3 metrics inside cards (admin summary style)
  document.querySelectorAll('.ant-card .ant-card-body').forEach((body) => {
    const h3 = body.querySelector('h3');
    const titleEl = body.querySelector('.ant-typography');
    if (h3 && !stats.some((s) => s.includes(h3.textContent?.trim() || '___'))) {
      // Get plain numbers rendered alongside the h3
      const siblings = Array.from(body.children);
      siblings.forEach((child) => {
        if (child.tagName === 'H3') return;
        const val = child.textContent?.trim();
        if (val && h3.textContent) stats.push(`| ${h3.textContent.trim()} | ${val} |`);
      });
    }
  });
  if (stats.length) {
    md.push('');
    md.push('## Key Metrics');
    md.push('| Metric | Value |');
    md.push('|--------|-------|');
    stats.forEach((s) => md.push(s));
  }

  /* ── Descriptions (label / value pairs) ─────────────────────────── */
  const descPairs: string[] = [];
  document.querySelectorAll('.ant-descriptions-item').forEach((el) => {
    const label = el.querySelector('.ant-descriptions-item-label')?.textContent?.trim();
    const value = el.querySelector('.ant-descriptions-item-content')?.textContent?.trim();
    if (label && value) descPairs.push(`- **${label}:** ${value}`);
  });
  if (descPairs.length) {
    md.push('');
    md.push('## Details');
    descPairs.forEach((p) => md.push(p));
  }

  /* ── Buttons (primary / action) ─────────────────────────────────── */
  const btns: string[] = [];
  document.querySelectorAll('button.ant-btn').forEach((btn) => {
    const label = btn.textContent?.trim();
    if (!label || label.length > 40) return;
    // skip chatbot's own buttons
    if (/send|clear chat/i.test(label)) return;
    const isPrimary = btn.classList.contains('ant-btn-primary');
    const isDisabled = btn.hasAttribute('disabled');
    let tag = isPrimary ? '`[primary]`' : '';
    if (isDisabled) tag += ' *(disabled)*';
    btns.push(`- ${label} ${tag}`.trim());
  });
  if (btns.length) {
    md.push('');
    md.push('## Buttons');
    btns.forEach((b) => md.push(b));
  }

  /* ── Radio groups (selected option) ─────────────────────────────── */
  const radios: string[] = [];
  document.querySelectorAll('.ant-radio-group').forEach((group) => {
    const options: string[] = [];
    group.querySelectorAll('.ant-radio-button-wrapper, .ant-radio-wrapper').forEach((opt) => {
      const label = opt.textContent?.trim();
      if (!label) return;
      const checked =
        opt.classList.contains('ant-radio-button-wrapper-checked') ||
        opt.querySelector('input[type="radio"]:checked') !== null;
      options.push(checked ? `**${label}**` : label);
    });
    if (options.length) radios.push(`- ${options.join(' / ')}`);
  });
  if (radios.length) {
    md.push('');
    md.push('## Filter Selections');
    radios.forEach((r) => md.push(r));
  }

  /* ── Select dropdowns (current value) ──────────────────────────── */
  const selects: string[] = [];
  document.querySelectorAll('.ant-select').forEach((sel) => {
    const value = sel.querySelector('.ant-select-selection-item')?.textContent?.trim();
    // Try to get a label from a preceding label element or form item
    const formItem = sel.closest('.ant-form-item');
    const label = formItem?.querySelector('.ant-form-item-label')?.textContent?.trim() || '';
    if (value) selects.push(`- ${label ? label + ': ' : ''}${value}`);
  });
  if (selects.length) {
    md.push('');
    md.push('## Dropdowns');
    selects.forEach((s) => md.push(s));
  }

  /* ── Collapse panels (open / closed) ───────────────────────────── */
  const panels: string[] = [];
  document.querySelectorAll('.ant-collapse-item').forEach((item) => {
    const header = item.querySelector('.ant-collapse-header-text, .ant-collapse-header')?.textContent?.trim();
    const isOpen = item.classList.contains('ant-collapse-item-active');
    if (header) panels.push(`- ${header} ${isOpen ? '*(open)*' : '*(collapsed)*'}`);
  });
  if (panels.length) {
    md.push('');
    md.push('## Panels');
    panels.forEach((p) => md.push(p));
  }

  /* ── Tags (account tags, selected partner) ─────────────────────── */
  const tags: string[] = [];
  document.querySelectorAll('.ant-tag').forEach((tag) => {
    const t = tag.textContent?.trim();
    if (t) tags.push(t);
  });
  if (tags.length) {
    md.push('');
    md.push('## Tags');
    md.push(tags.join(', '));
  }

  /* ── Tables – markdown format ──────────────────────────────────── */
  document.querySelectorAll('.ant-table-wrapper, table').forEach((wrapper) => {
    const headers: string[] = [];
    wrapper.querySelectorAll('thead th').forEach((th) => {
      headers.push(th.textContent?.trim()?.replace(/\|/g, '/') || '');
    });
    if (!headers.length) return;

    md.push('');
    md.push('## Table');
    md.push('| ' + headers.join(' | ') + ' |');
    md.push('| ' + headers.map(() => '---').join(' | ') + ' |');

    let rowCount = 0;
    wrapper.querySelectorAll('tbody tr').forEach((tr) => {
      if (rowCount >= 15) return;
      // skip empty / no-data rows
      if (tr.querySelector('.ant-empty')) return;
      const cells: string[] = [];
      tr.querySelectorAll('td').forEach((td) =>
        cells.push(td.textContent?.trim()?.replace(/\|/g, '/') || ''),
      );
      if (cells.length && cells.some((c) => c)) {
        md.push('| ' + cells.join(' | ') + ' |');
        rowCount++;
      }
    });

    // Pagination info
    const pag = wrapper.querySelector('.ant-pagination');
    if (pag) {
      const total = pag.querySelector('.ant-pagination-total-text')?.textContent?.trim();
      if (total) md.push(`*${total}*`);
    }
  });

  /* ── Alerts / Badges ───────────────────────────────────────────── */
  document.querySelectorAll('.ant-alert').forEach((alert) => {
    const msg = alert.querySelector('.ant-alert-message')?.textContent?.trim();
    if (msg) md.push(`\n> ⚠️ ${msg}`);
  });

  /* ── Modals (if open) ──────────────────────────────────────────── */
  document.querySelectorAll('.ant-modal-wrap').forEach((wrap) => {
    if ((wrap as HTMLElement).style.display === 'none') return;
    const title = wrap.querySelector('.ant-modal-title')?.textContent?.trim();
    if (title) {
      md.push('');
      md.push(`## Open Modal: ${title}`);
    }
  });

  /* ── Final assembly & cap ──────────────────────────────────────── */
  let result = md.join('\n');
  if (result.length > 4000) result = result.slice(0, 4000) + '\n\n*(truncated)*';
  return result;
}

/**
 * Render chat content. AI messages use ReactMarkdown for rich formatting
 * (tables, lists, bold). User messages render as plain text with clickable links.
 */
function renderBubbleContent(text: string, isUser: boolean): React.ReactNode {
  if (isUser) {
    // Plain text with clickable URLs for user messages
    const urlRegex = /(https?:\/\/[^\s),]+)/g;
    const parts = text.split(urlRegex);
    if (parts.length === 1) return text;
    return parts.map((part, i) =>
      urlRegex.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#b7e3ff', textDecoration: 'underline' }}
        >
          {part}
        </a>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      ),
    );
  }

  // AI messages: render as Markdown
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1677ff', textDecoration: 'underline' }}>
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div style={{ overflowX: 'auto', margin: '6px 0' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'inherit' }}>
              {children}
            </table>
          </div>
        ),
        th: ({ children }) => (
          <th style={{ border: '1px solid #ddd', padding: '4px 8px', background: '#fafafa', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td style={{ border: '1px solid #eee', padding: '4px 8px' }}>
            {children}
          </td>
        ),
        ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ul>,
        ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ol>,
        li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
        p: ({ children }) => <p style={{ margin: '4px 0' }}>{children}</p>,
        strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
        h1: ({ children }) => <div style={{ fontWeight: 700, fontSize: '1.1em', margin: '6px 0 2px' }}>{children}</div>,
        h2: ({ children }) => <div style={{ fontWeight: 700, fontSize: '1.05em', margin: '6px 0 2px' }}>{children}</div>,
        h3: ({ children }) => <div style={{ fontWeight: 600, margin: '4px 0 2px' }}>{children}</div>,
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

/* ------------------------------------------------------------------ */
/*  Browser action system – parse [ACTION:xxx] tags and execute them   */
/* ------------------------------------------------------------------ */

/**
 * Extract an [ACTION:name] tag from the end of an AI response.
 * Returns the cleaned text and the action name (if any).
 */
function extractAction(reply: string): { text: string; action: string | null } {
  const match = reply.match(/\[ACTION:(\w+)\]/);
  if (!match) return { text: reply, action: null };
  return {
    text: reply.replace(/\[ACTION:\w+\]/, '').trim(),
    action: match[1],
  };
}

/**
 * Map action names to button text on the page and programmatically click them.
 */
const ACTION_BUTTON_MAP: Record<string, string> = {
  openEMDFirmList: 'EMD Firm List',
  openRedemptionReport: 'Redemption Report',
  openTrailerFeesReport: 'Trailer Fees Report',
  openVIPList: 'Generate VIP List',
  openT5NR4Report: 'Generate T5/NR4',
  openAdvisorList: 'Advisor List',
  openClientList: 'Client List',
};

/** Actions that should auto-click the modal's OK / Generate button after opening */
const AUTO_GENERATE_ACTIONS = new Set([
  'openRedemptionReport',
  'openTrailerFeesReport',
  'openVIPList',
  'openT5NR4Report',
]);

/**
 * Find and click a visible button by its text content.
 * Returns true if the button was found and clicked.
 */
function clickButtonByText(text: string): boolean {
  const normalise = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
  const target = normalise(text);
  const buttons = document.querySelectorAll('button.ant-btn');
  for (const btn of Array.from(buttons)) {
    const el = btn as HTMLElement;
    const btnText = normalise(el.textContent || '');
    if (btnText === target && !el.hasAttribute('disabled') && el.offsetParent !== null) {
      console.log(`[ChatbotAction] Clicking button: "${text}"`);
      el.click();
      return true;
    }
  }
  console.warn(`[ChatbotAction] No matching button found for: "${text}". Visible buttons:`,
    Array.from(buttons).map(b => (b as HTMLElement).textContent?.trim()));
  return false;
}

/**
 * Wait for a modal to appear and then click its OK/Generate button.
 * Polls every 300ms for up to 3 seconds.
 */
function waitForModalAndClickOk(maxWait = 3000): void {
  const start = Date.now();
  const poll = () => {
    // Look for a visible modal footer's primary button (the OK / Generate button)
    const modals = document.querySelectorAll('.ant-modal-wrap');
    for (const wrap of Array.from(modals)) {
      const el = wrap as HTMLElement;
      if (el.style.display === 'none') continue;
      const okBtn = el.querySelector('.ant-modal-footer button.ant-btn-primary') as HTMLElement | null;
      if (okBtn && !okBtn.hasAttribute('disabled') && okBtn.offsetParent !== null) {
        console.log(`[ChatbotAction] Auto-clicking modal Generate/OK button: "${okBtn.textContent?.trim()}"`);
        okBtn.click();
        return;
      }
    }
    if (Date.now() - start < maxWait) {
      setTimeout(poll, 300);
    } else {
      console.warn('[ChatbotAction] Modal OK button not found within timeout');
    }
  };
  poll();
}

function executeAction(actionName: string): void {
  const buttonText = ACTION_BUTTON_MAP[actionName];
  if (!buttonText) {
    console.warn(`[ChatbotAction] Unknown action: ${actionName}`);
    return;
  }

  const clicked = clickButtonByText(buttonText);
  if (!clicked) {
    console.warn(`[ChatbotAction] Button not found or disabled: "${buttonText}"`);
    return;
  }

  // For report/generate actions, also click the Generate button inside the modal
  if (AUTO_GENERATE_ACTIONS.has(actionName)) {
    // Wait for the modal to fully render, then click its OK/Generate button
    setTimeout(() => waitForModalAndClickOk(), 800);
  }
}

const ChatbotWidget: React.FC<ChatbotWidgetProps> = ({ userEmail, userType }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [chatTurns, setChatTurns] = useState<ChatTurn[]>([]);
  const [recommendations, setRecommendations] = useState<string[]>(getDefaultRecommendations(userType));
  const [pageContext, setPageContext] = useState<string>('');
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  /** After a report action generates a CSV download, store it for optional emailing */
  const [pendingEmailCSV, setPendingEmailCSV] = useState<{ csvContent: string; filename: string } | null>(null);
  const [emailSending, setEmailSending] = useState(false);

  /** Ref to hold the MutationObserver cleanup function for CSV capture */
  const csvCaptureCleanupRef = useRef<(() => void) | null>(null);

  /* draggable panel state */
  const [panelPos, setPanelPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  /* font size control (min 10, max 20, default 13) */
  const [fontSize, setFontSize] = useState(14);
  const decreaseFont = (e: React.MouseEvent) => { e.stopPropagation(); setFontSize((s) => Math.max(10, s - 1)); };
  const increaseFont = (e: React.MouseEvent) => { e.stopPropagation(); setFontSize((s) => Math.min(20, s + 1)); };

  const listRef = useRef<HTMLDivElement | null>(null);

  const storageKey = useMemo(
    () => `ginkgo_chatbot_history_${userEmail || 'anonymous'}`,
    [userEmail],
  );

  /* restore / persist history */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setChatTurns(parsed.slice(-20));
      }
    } catch (e) { console.error('Unable to load chatbot history:', e); }
  }, [storageKey]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(chatTurns.slice(-20)));
  }, [chatTurns, storageKey]);

  /* auto-scroll */
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [chatTurns, loading]);

  /* submit */
  const submitMessage = async (message: string) => {
    const trimmed = message.trim();
    if (!trimmed || loading) return;

    // Re-scrape page data fresh before every submission, then strip PII
    const freshContext = scrapePageContext(userType);
    setPageContext(freshContext);

    const submitTime = new Date().toISOString();
    setChatTurns((prev) => [...prev, { role: 'user', content: trimmed, timestamp: submitTime }]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${baseURL}/chatbot/message`,
        { message: trimmed, pageContext: freshContext || undefined, userType: userType || undefined, timestamp: submitTime },
        { headers: { Accept: 'application/json' } },
      );

      const rawReply = normalizeReplyText(response?.data?.reply) || 'No response received.';
      const replyTime = new Date().toISOString();

      // Extract and execute any [ACTION:xxx] tag from the reply
      const { text: replyToShow, action } = extractAction(rawReply);
      setChatTurns((prev) => [...prev, { role: 'ai', content: replyToShow, timestamp: replyTime }]);

      if (action) {
        if (AUTO_GENERATE_ACTIONS.has(action)) {
          // Report actions require Yes/No confirmation
          setPendingAction(action);
          const label = ACTION_BUTTON_MAP[action] || action;
          setChatTurns((prev) => [
            ...prev,
            { role: 'ai', content: `Would you like to proceed with **${label}**?`, timestamp: replyTime },
          ]);
        } else {
          // Non-report actions execute immediately
          setTimeout(() => executeAction(action), 600);
        }
      }

      if (Array.isArray(response?.data?.recommendations) && response.data.recommendations.length > 0) {
        setRecommendations(response.data.recommendations.map((i: any) => String(i)));
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Chatbot request failed.';
      setChatTurns((prev) => [...prev, { role: 'ai', content: String(msg) }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitMessage(inputValue);
    }
  };

  /**
   * Start listening for CSV file downloads.
   * Uses a MutationObserver on document.body to detect <a> elements
   * with .csv in their download attribute. Reads the blob URL content
   * before it's revoked.
   */
  const startCSVCapture = () => {
    // Clean up any previous observer
    if (csvCaptureCleanupRef.current) {
      csvCaptureCleanupRef.current();
      csvCaptureCleanupRef.current = null;
    }

    let captured = false;

    const observer = new MutationObserver((mutations) => {
      if (captured) return;
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (
            node instanceof HTMLAnchorElement &&
            node.download &&
            node.download.toLowerCase().endsWith('.csv') &&
            node.href
          ) {
            captured = true;
            observer.disconnect();
            const filename = node.download;
            // Fetch the blob content before it's revoked (typically revoked after 100ms)
            fetch(node.href)
              .then((r) => r.text())
              .then((csvText) => {
                console.log('[ChatbotCSV] Captured CSV download:', filename, `(${csvText.length} chars)`);
                setPendingEmailCSV({ csvContent: csvText, filename });
                const ts = new Date().toISOString();
                setChatTurns((prev) => [
                  ...prev,
                  {
                    role: 'ai',
                    content: `Report **${filename}** has been downloaded. Would you like to send it to your email as well?`,
                    timestamp: ts,
                  },
                ]);
              })
              .catch((err) => {
                console.warn('[ChatbotCSV] Failed to read CSV blob:', err);
              });
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true });

    // Auto-cleanup after 30 seconds if no CSV was captured
    const timeout = setTimeout(() => {
      if (!captured) {
        observer.disconnect();
        console.log('[ChatbotCSV] CSV capture timed out');
      }
    }, 30000);

    csvCaptureCleanupRef.current = () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  };

  /* Yes/No confirmation handlers for report actions */
  const confirmAction = () => {
    if (!pendingAction) return;
    const actionToRun = pendingAction;
    setChatTurns((prev) => [
      ...prev,
      { role: 'user', content: 'Yes', timestamp: new Date().toISOString() },
    ]);
    setPendingAction(null);

    // Start listening for CSV downloads before executing the action
    startCSVCapture();
    setTimeout(() => executeAction(actionToRun), 600);
  };

  const cancelAction = () => {
    setChatTurns((prev) => [
      ...prev,
      { role: 'user', content: 'No', timestamp: new Date().toISOString() },
      { role: 'ai', content: 'Action cancelled.', timestamp: new Date().toISOString() },
    ]);
    setPendingAction(null);
  };

  /* Email CSV handlers */
  const confirmEmailCSV = async () => {
    if (!pendingEmailCSV) return;
    const { csvContent, filename } = pendingEmailCSV;
    setPendingEmailCSV(null);
    setEmailSending(true);
    setChatTurns((prev) => [
      ...prev,
      { role: 'user', content: 'Yes, send by email', timestamp: new Date().toISOString() },
    ]);

    try {
      const response = await axios.post(
        `${baseURL}/chatbot/email-report`,
        { csvContent, filename },
        { headers: { Accept: 'application/json' }, timeout: 60000 },
      );
      const msg = response?.data?.message || `Report sent to your email.`;
      setChatTurns((prev) => [
        ...prev,
        { role: 'ai', content: `✅ ${msg}`, timestamp: new Date().toISOString() },
      ]);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to send email.';
      setChatTurns((prev) => [
        ...prev,
        { role: 'ai', content: `❌ ${String(msg)}`, timestamp: new Date().toISOString() },
      ]);
    } finally {
      setEmailSending(false);
    }
  };

  const cancelEmailCSV = () => {
    setPendingEmailCSV(null);
    setChatTurns((prev) => [
      ...prev,
      { role: 'user', content: 'No thanks', timestamp: new Date().toISOString() },
    ]);
  };

  /* clear history (local + server) */
  const clearChat = async () => {
    setChatTurns([]);
    setRecommendations(getDefaultRecommendations(userType));
    setPendingEmailCSV(null);
    setPendingAction(null);
    if (csvCaptureCleanupRef.current) {
      csvCaptureCleanupRef.current();
      csvCaptureCleanupRef.current = null;
    }
    localStorage.removeItem(storageKey);
    try {
      await axios.post(`${baseURL}/chatbot/clear`, {}, { headers: { Accept: 'application/json' } });
    } catch (_) { /* best-effort */ }
  };

  /* ---- drag handlers -------------------------------------------- */
  const onDragStart = (e: React.MouseEvent) => {
    // Only left-click on header
    if (e.button !== 0) return;
    e.preventDefault();
    const panelEl = (e.target as HTMLElement).closest('[data-chatbot-panel]') as HTMLElement | null;
    if (!panelEl) return;
    const rect = panelEl.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: rect.left,
      origY: rect.top,
    };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      setPanelPos({
        x: Math.max(0, Math.min(window.innerWidth - 400, dragRef.current.origX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - 100, dragRef.current.origY + dy)),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  /* ---- render ---------------------------------------------------- */
  return (
    <>
      <FloatButton
        icon={<img src={GinkgoHouseIcon} alt="Ginkgo" style={{ width: 28, height: 28, borderRadius: '50%' }} />}
        tooltip="Open assistant"
        type="primary"
        onClick={() => {
          // Capture page data on every open so the AI always has fresh context
          setPageContext(scrapePageContext(userType));
          setOpen(true);
        }}
      />

      {open && (
        <div
          data-chatbot-panel
          style={{
            ...PANEL,
            ...(panelPos
              ? { left: panelPos.x, top: panelPos.y, right: 'auto', bottom: 'auto' }
              : {}),
          }}
        >
          {/* header – drag handle */}
          <div style={{ ...HEADER, cursor: 'move' }} onMouseDown={onDragStart}>
            <span><img src={GinkgoHouseIcon} alt="Ginkgo" style={{ width: 18, height: 18, borderRadius: '50%', marginRight: 6, verticalAlign: 'middle' }} />Ginkgo Chatbot</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* font size controls */}
              <span
                title="Decrease font size"
                onClick={decreaseFont}
                style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: fontSize <= 10 ? 0.4 : 1, userSelect: 'none' }}
              >A</span>
              <span
                title="Increase font size"
                onClick={increaseFont}
                style={{ cursor: 'pointer', fontSize: 16, fontWeight: 700, opacity: fontSize >= 20 ? 0.4 : 1, userSelect: 'none' }}
              >A</span>
              <DeleteOutlined
                title="Clear chat"
                style={{ cursor: 'pointer', fontSize: 14 }}
                onClick={clearChat}
              />
              <CloseOutlined style={{ cursor: 'pointer' }} onClick={() => setOpen(false)} />
            </span>
          </div>

          {/* message list */}
          <div ref={listRef} style={MSG_LIST}>
            {/* welcome + recommendation links (always visible at top) */}
            {chatTurns.length === 0 && (
              <div style={{ textAlign: 'center', padding: '12px 0', color: '#999' }}>
                <img src={GinkgoHouseIcon} alt="Ginkgo" style={{ width: 28, height: 28, borderRadius: '50%', display: 'block', marginBottom: 4, margin: '0 auto 4px' }} />
                <div style={{ fontWeight: 600, marginBottom: 2 }}>How can I help?</div>
                <div style={{ fontSize: 12 }}>Ask portfolio, advisor, or database questions.</div>
              </div>
            )}
            {/* quick-ask links */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', padding: '6px 0' }}>
              {recommendations.map((rec, i) => (
                <span
                  key={i}
                  onClick={() => submitMessage(rec)}
                  style={{
                    background: '#e6f4ff',
                    border: '1px solid #91caff',
                    borderRadius: 6,
                    padding: '3px 8px',
                    fontSize: 12,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    color: '#1677ff',
                    opacity: loading ? 0.5 : 1,
                  }}
                >
                  {rec}
                </span>
              ))}
            </div>

            {chatTurns.map((turn, idx) => {
              const isUser = turn.role === 'user';
              const timeLabel = formatChatTimeLabel(turn.timestamp);
              const dateLabel = formatChatDateLabel(turn.timestamp);
              const currentDayKey = getDayKey(turn.timestamp);
              const previousDayKey = getDayKey(chatTurns[idx - 1]?.timestamp);
              const showDateOnQuestion = isUser && !!dateLabel && currentDayKey !== previousDayKey;

              return (
                <div key={`${turn.role}-${idx}`} style={{ marginBottom: 10 }}>
                  {showDateOnQuestion && (
                    <div style={{
                      fontSize: 10,
                      color: '#aaa',
                      textAlign: 'right',
                      paddingRight: 36,
                      marginBottom: 2,
                    }}>
                      {dateLabel}
                    </div>
                  )}
                  <div
                    style={{ ...BUBBLE_ROW, flexDirection: isUser ? 'row-reverse' : 'row', marginBottom: 0 }}
                  >
                    <div
                      style={{
                        ...AVATAR,
                        background: isUser ? '#1677ff' : '#f0f0f0',
                        color: isUser ? '#fff' : '#333',
                      }}
                    >
                      {isUser ? <UserOutlined /> : <img src={GinkgoHouseIcon} alt="Ginkgo" style={{ width: 20, height: 20, borderRadius: '50%' }} />}
                    </div>
                    <div
                      style={{
                        ...BUBBLE_TEXT,
                        fontSize,
                        background: isUser ? '#1677ff' : '#f5f5f5',
                        color: isUser ? '#fff' : '#222',
                      }}
                    >
                      {renderBubbleContent(turn.content, isUser)}
                    </div>
                  </div>
                  {timeLabel && (
                    <div style={{
                      fontSize: 10,
                      color: '#aaa',
                      textAlign: isUser ? 'right' : 'left',
                      paddingLeft: isUser ? 0 : 36,
                      paddingRight: isUser ? 36 : 0,
                      marginTop: 2,
                    }}>
                      {timeLabel}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Yes / No confirmation buttons for pending report actions */}
            {pendingAction && !loading && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: '8px 0' }}>
                <Button type="primary" size="small" onClick={confirmAction}>Yes</Button>
                <Button size="small" onClick={cancelAction}>No</Button>
              </div>
            )}

            {/* Email CSV prompt — shown after a CSV file is downloaded */}
            {pendingEmailCSV && !pendingAction && !loading && !emailSending && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', padding: '8px 0' }}>
                <Button
                  type="primary"
                  size="small"
                  icon={<MailOutlined />}
                  onClick={confirmEmailCSV}
                >
                  Yes, send email
                </Button>
                <Button size="small" onClick={cancelEmailCSV}>No thanks</Button>
              </div>
            )}

            {(loading || emailSending) && (
              <div style={{ textAlign: 'center', padding: 6 }}>
                <Spin size="small" />
                {emailSending && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Sending email...</div>}
              </div>
            )}
          </div>

          {/* disclaimer */}
          <div style={{ textAlign: 'center', padding: '2px 12px 0', flexShrink: 0 }}>
            <span style={{ fontSize: 13, color: '#999', fontStyle: 'italic' }}>
              Answers are generated by AI and are for reference only.
            </span>
          </div>

          {/* input */}
          <div style={INPUT_ROW}>
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything..."
              disabled={loading}
              style={{ flex: 1 }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => submitMessage(inputValue)}
              loading={loading}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
